import React, { SyntheticEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Link } from '~/lib/react-router-dom';

import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Pagination,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';

import { useGetChainRatePlans } from '~/hooks/apis/reservation/useChainRatePlans';
import type { ChainRatePlan, RatePlanStatus } from '~/hooks/apis/reservation/useChainRatePlans';
import useDebounced from '~/hooks/useDebounced';

import MissingBadge from './MissingBadge';
import StatusChip from './StatusChip';

// Maps display label -> all Salesforce chain IDs that share that brand.
// These IDs come from the Salesforce CRM (Account object, chain-level accounts).
// To find or update them: search Salesforce for chain-level accounts and copy the 18-char Account ID.
// TODO: Consider fetching chain configuration from a backend endpoint to avoid hardcoding.
export const CHAIN_GROUPS: Record<string, string[]> = {
  IHG: ['00128000005hFxHAAU'],
  ACCOR: ['0012800000cuN6wAAE', '0010I00001ggeAKQAY', '00128000005cHQ5AAM', '00128000005c1TkAAI'],
  HYATT: ['0012800000cuJr4AAE'],
  OMNI: ['0010I00002GgAOeQAN'],
  PROPERTYCONNECT: ['0012800000cuKR1AAM'],
};

// Reverse lookup: Salesforce ID -> display label (kept for external consumers)
export const CHAIN_NAMES: Record<string, string> = Object.fromEntries(
  Object.entries(CHAIN_GROUPS).flatMap(([label, ids]) => ids.map((id) => [id, label])),
);

const PER_PAGE = 50;
const DEBOUNCE_MS = 400;

const headerCellSx = {
  fontWeight: 500,
  fontSize: 11,
  textTransform: 'uppercase',
  letterSpacing: 0.5,
  color: 'text.secondary',
} as const;

interface RatePlanGroup {
  code: string;
  name: string;
  plans: ChainRatePlan[];
}

function groupByCode(plans: ChainRatePlan[]): RatePlanGroup[] {
  const map = new Map<string, RatePlanGroup>();
  for (const plan of plans) {
    const key = plan.rate_plan_code ?? `__nocode__${plan.id}`;
    if (!map.has(key)) {
      map.set(key, { code: plan.rate_plan_code ?? '—', name: plan.name, plans: [] });
    }
    map.get(key)!.plans.push(plan);
  }
  return Array.from(map.values());
}

function completionBadge(plans: ChainRatePlan[]) {
  const total = plans.length;
  const complete = plans.filter((p) => p.markup != null && p.rate_type != null).length;
  if (complete === total) return { label: `✓ ${complete}/${total}`, color: 'success' as const };
  if (complete === 0) return { label: `✗ 0/${total}`, color: 'error' as const };
  return { label: `◑ ${complete}/${total}`, color: 'warning' as const };
}

export default function ChainsTab() {
  const [selectedGroup, setSelectedGroup] = useState<string>('');
  const [statusTab, setStatusTab] = useState<RatePlanStatus>('content-approved');
  const [page, setPage] = useState(1);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [propertyFilter, setPropertyFilter] = useState('');
  const [codeFilter, setCodeFilter] = useState('');

  const debouncedCodeFilter = useDebounced(codeFilter, DEBOUNCE_MS);
  const debouncedPropertyFilter = useDebounced(propertyFilter, DEBOUNCE_MS);

  // Reset to page 1 when filters change
  const prevFilters = useRef({ debouncedCodeFilter, debouncedPropertyFilter });
  useEffect(() => {
    if (
      prevFilters.current.debouncedCodeFilter !== debouncedCodeFilter ||
      prevFilters.current.debouncedPropertyFilter !== debouncedPropertyFilter
    ) {
      setPage(1);
      setExpandedGroups(new Set());
      prevFilters.current = { debouncedCodeFilter, debouncedPropertyFilter };
    }
  }, [debouncedCodeFilter, debouncedPropertyFilter]);

  const handleChainChange = useCallback((e: SelectChangeEvent<string>) => {
    setSelectedGroup(e.target.value);
    setPage(1);
    setExpandedGroups(new Set());
  }, []);

  const handleStatusTabChange = useCallback((_: SyntheticEvent, value: RatePlanStatus) => {
    setStatusTab(value);
    setPage(1);
    setExpandedGroups(new Set());
  }, []);

  const handlePageChange = useCallback((_: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
    setExpandedGroups(new Set());
  }, []);

  const toggleGroup = useCallback((code: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      next.has(code) ? next.delete(code) : next.add(code);
      return next;
    });
  }, []);

  const chainIds = selectedGroup ? CHAIN_GROUPS[selectedGroup] ?? [] : [];

  const { ratePlans, totalCount, isLoading, error } = useGetChainRatePlans(chainIds, {
    status: statusTab,
    page,
    limit: PER_PAGE,
    ratePlanCode: debouncedCodeFilter || undefined,
    propertySearch: debouncedPropertyFilter || undefined,
  });

  const pageCount = Math.ceil(totalCount / PER_PAGE);

  const groups = useMemo(() => groupByCode(ratePlans), [ratePlans]);

  const handleReset = useCallback(() => {
    setCodeFilter('');
    setPropertyFilter('');
  }, []);

  return (
    <Stack spacing={2}>
      {/* ── Filter card ── */}
      <Paper variant="outlined" sx={{ p: 2.5 }}>
        <Typography variant="overline" sx={{ display: 'block', color: 'text.secondary', mb: 1.5, letterSpacing: 1 }}>
          Filter rates
        </Typography>
        <Stack direction="row" spacing={1.5} flexWrap="wrap" alignItems="flex-end">
          <FormControl size="small" sx={{ minWidth: 200, flexGrow: 1 }}>
            <InputLabel>Supplier Chain</InputLabel>
            <Select value={selectedGroup} label="Supplier Chain" onChange={handleChainChange}>
              <MenuItem value="">
                <em>All chains</em>
              </MenuItem>
              {Object.keys(CHAIN_GROUPS).map((label) => (
                <MenuItem key={label} value={label}>
                  {label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            size="small"
            label="Property ID / Name"
            placeholder="e.g. Crowne Plaza"
            value={propertyFilter}
            onChange={(e) => setPropertyFilter(e.target.value)}
            sx={{ minWidth: 220, flexGrow: 1 }}
          />

          <TextField
            size="small"
            label="Rate Plan Code"
            placeholder="e.g. FLEXBB25"
            value={codeFilter}
            onChange={(e) => setCodeFilter(e.target.value)}
            sx={{ minWidth: 180, flexGrow: 1 }}
          />

          <Button variant="outlined" size="small" onClick={handleReset} sx={{ height: 40 }}>
            Reset
          </Button>
        </Stack>
      </Paper>

      {selectedGroup && (
        <>
          {/* ── Sub-tabs ── */}
          <Tabs value={statusTab} onChange={handleStatusTabChange} sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tab
              value="content-approved"
              label={
                <Stack direction="row" alignItems="center" spacing={1}>
                  <span>Rate Plans – Content-Approved</span>
                  {statusTab === 'content-approved' && totalCount > 0 && (
                    <Chip label={totalCount} size="small" color="primary" sx={{ height: 20, fontSize: 11 }} />
                  )}
                </Stack>
              }
            />
            <Tab
              value="draft"
              label={
                <Stack direction="row" alignItems="center" spacing={1}>
                  <span>Rate Plans – Draft</span>
                  {statusTab === 'draft' && totalCount > 0 && (
                    <Chip label={totalCount} size="small" color="warning" sx={{ height: 20, fontSize: 11 }} />
                  )}
                </Stack>
              }
            />
          </Tabs>

          {/* ── Multi-ID pagination warning ── */}
          {chainIds.length > 1 && (
            <Alert severity="warning" sx={{ py: 0.5, fontSize: 13 }}>
              <strong>{selectedGroup}</strong> maps to {chainIds.length} Salesforce IDs. Pagination totals may be
              inaccurate until a multi-chain API endpoint is available.
            </Alert>
          )}

          {/* ── Info banner ── */}
          {!isLoading && !error && (
            <Alert severity={statusTab === 'content-approved' ? 'info' : 'warning'} sx={{ py: 0.5, fontSize: 13 }}>
              {statusTab === 'content-approved' ? (
                <>
                  Rate plans created by <strong>syncMissingProducts</strong> as <code>content-approved</code> (IHG).
                  Fill missing fields and bulk-save.
                </>
              ) : (
                <>
                  Rate plans created as <strong>draft</strong> (non-IHG). Fill all required fields and bulk-save to mark
                  ready for review.
                </>
              )}
            </Alert>
          )}

          {/* ── Loading / error / empty ── */}
          {isLoading && (
            <Box display="flex" justifyContent="center" py={6}>
              <CircularProgress />
            </Box>
          )}

          {error && !isLoading && <Alert severity="error">Failed to load rate plans.</Alert>}

          {!isLoading && !error && ratePlans.length === 0 && (
            <Box textAlign="center" py={8} color="text.secondary">
              <Typography>No rate plans found for this chain.</Typography>
            </Box>
          )}

          {/* ── Grouped table ── */}
          {!isLoading && groups.length > 0 && (
            <Paper variant="outlined" sx={{ overflow: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell sx={{ width: 32 }} />
                    <TableCell sx={{ ...headerCellSx, whiteSpace: 'nowrap' }}>Rate Plan Code</TableCell>
                    <TableCell sx={headerCellSx}>Name</TableCell>
                    <TableCell sx={headerCellSx}>Completion</TableCell>
                    <TableCell sx={headerCellSx}>Properties</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {groups.map((group) => {
                    const isExpanded = expandedGroups.has(group.code);
                    const badge = completionBadge(group.plans);
                    return (
                      <React.Fragment key={group.code}>
                        {/* Group header row */}
                        <TableRow
                          sx={{
                            bgcolor: isExpanded ? '#f0f7ff' : 'grey.50',
                            borderTop: '2px solid',
                            borderColor: isExpanded ? 'primary.light' : 'divider',
                            cursor: 'pointer',
                            '&:hover': { bgcolor: '#e8f2fc' },
                          }}
                          onClick={() => toggleGroup(group.code)}
                        >
                          <TableCell sx={{ py: 0.75, pl: 1, pr: 0 }}>
                            <IconButton size="small" sx={{ p: 0.25 }}>
                              <KeyboardArrowRightIcon
                                fontSize="small"
                                sx={{
                                  transition: 'transform 0.2s',
                                  transform: isExpanded ? 'rotate(90deg)' : 'none',
                                  color: isExpanded ? 'primary.main' : 'text.secondary',
                                }}
                              />
                            </IconButton>
                          </TableCell>
                          <TableCell sx={{ py: 0.75 }}>
                            <Typography
                              component="code"
                              sx={{
                                fontFamily: 'monospace',
                                fontSize: 12,
                                fontWeight: 600,
                                bgcolor: 'grey.200',
                                px: 0.75,
                                py: 0.25,
                                borderRadius: 0.5,
                                color: 'grey.800',
                              }}
                            >
                              {group.code}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ py: 0.75, color: 'grey.800', fontSize: 13 }}>{group.name}</TableCell>
                          <TableCell sx={{ py: 0.75 }}>
                            <Chip
                              label={badge.label}
                              size="small"
                              color={badge.color}
                              sx={{ height: 20, fontSize: 11, fontWeight: 600, borderRadius: 5 }}
                            />
                          </TableCell>
                          <TableCell sx={{ py: 0.75, color: 'text.secondary', fontSize: 12 }}>
                            {group.plans.length} {group.plans.length === 1 ? 'property' : 'properties'}
                          </TableCell>
                        </TableRow>

                        {/* Expanded plan rows */}
                        {isExpanded && (
                          <TableRow>
                            <TableCell colSpan={5} sx={{ py: 0, px: 0, borderBottom: 'none' }}>
                              <Table size="small" sx={{ width: '100%', tableLayout: 'fixed' }}>
                                <colgroup>
                                  <col style={{ width: '30%' }} />
                                  <col style={{ width: '12%' }} />
                                  <col style={{ width: '15%' }} />
                                  <col style={{ width: '15%' }} />
                                  <col style={{ width: '20%' }} />
                                  <col style={{ width: '8%' }} />
                                </colgroup>
                                <TableHead>
                                  <TableRow sx={{ bgcolor: 'grey.100' }}>
                                    <TableCell sx={{ ...headerCellSx, pl: 5.5, color: 'text.disabled' }}>
                                      Property
                                    </TableCell>
                                    <TableCell sx={{ ...headerCellSx, color: 'text.disabled' }}>Fields</TableCell>
                                    <TableCell sx={{ ...headerCellSx, color: 'text.disabled' }}>Markup %</TableCell>
                                    <TableCell sx={{ ...headerCellSx, color: 'text.disabled' }}>Rate Type</TableCell>
                                    <TableCell sx={{ ...headerCellSx, color: 'text.disabled' }}>Status</TableCell>
                                    <TableCell />
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {group.plans.map((plan) =>
                                    plan.properties.map((prop) => (
                                      <TableRow
                                        key={`${plan.id}-${prop.id}`}
                                        sx={{ bgcolor: '#fff', '&:hover': { bgcolor: '#f5f9ff' } }}
                                      >
                                        <TableCell sx={{ pl: 5.5, fontSize: 13, color: 'primary.main' }}>
                                          {prop.name}
                                        </TableCell>
                                        <TableCell>
                                          <MissingBadge markup={plan.markup} rateType={plan.rate_type} />
                                        </TableCell>
                                        <TableCell>
                                          <TextField
                                            size="small"
                                            type="number"
                                            defaultValue={plan.markup ?? ''}
                                            placeholder="e.g. 20"
                                            slotProps={{ htmlInput: { min: 0, max: 100, step: 0.5 } }}
                                            sx={{
                                              width: 90,
                                              '& .MuiInputBase-input': { fontSize: 13, py: 0.5, px: 1 },
                                              '& .MuiOutlinedInput-root': {
                                                '& fieldset': {
                                                  borderColor: plan.markup == null ? '#ef9a9a' : '#a5d6a7',
                                                },
                                              },
                                            }}
                                          />
                                        </TableCell>
                                        <TableCell>
                                          <FormControl size="small" sx={{ minWidth: 100 }}>
                                            <Select
                                              defaultValue={plan.rate_type ?? ''}
                                              displayEmpty
                                              sx={{
                                                fontSize: 13,
                                                '& .MuiSelect-select': { py: 0.5 },
                                                '& .MuiOutlinedInput-notchedOutline': {
                                                  borderColor: plan.rate_type == null ? '#ef9a9a' : '#a5d6a7',
                                                },
                                              }}
                                            >
                                              <MenuItem value="">
                                                <em>– select –</em>
                                              </MenuItem>
                                              <MenuItem value="gross">Gross</MenuItem>
                                              <MenuItem value="net">Net</MenuItem>
                                            </Select>
                                          </FormControl>
                                        </TableCell>
                                        <TableCell>
                                          <StatusChip status={plan.status} />
                                        </TableCell>
                                        <TableCell sx={{ pr: 1 }}>
                                          <IconButton
                                            size="small"
                                            component={Link}
                                            to={`/vendors/${plan.id_salesforce_external}/rate-plans/${plan.id}`}
                                            title="View rate plan"
                                          >
                                            <OpenInNewIcon fontSize="small" />
                                          </IconButton>
                                        </TableCell>
                                      </TableRow>
                                    )),
                                  )}
                                </TableBody>
                              </Table>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </Paper>
          )}

          {/* ── Pagination ── */}
          {pageCount > 0 && (
            <Box display="flex" justifyContent="center">
              <Pagination count={pageCount} page={page} onChange={handlePageChange} color="primary" />
            </Box>
          )}
        </>
      )}

      {/* Empty state when no chain selected */}
      {!selectedGroup && (
        <Box textAlign="center" py={10} color="text.secondary">
          <Typography variant="body2">Select a chain to view its rate plans.</Typography>
        </Box>
      )}
    </Stack>
  );
}
